// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The application shell (design doc §10.1, §12.1).
 *
 * The navigation is a left rail rather than a top bar. The sections are grouped by what
 * they are for — daily work, the master data behind it, the record — and a vertical list
 * has room to say so; a horizontal strip would have to flatten six destinations into one
 * undifferentiated row. It also leaves the full width of the page to tables that are wide
 * by nature: a BOM with its two quantity columns, an archive with position, serial,
 * quantity, origin and material code.
 *
 * The account lives at the foot of the rail, and the email is the button: clicking it opens
 * a menu with the two things you can do to a session — sign out, or hand the station to the
 * next person. They are genuinely different actions and used to be one. **Sign out** revokes
 * the session and stops at this app's sign-in page. **Switch user** revokes it and goes
 * straight into Google, which shows the account chooser (the server always sends
 * `prompt=select_account`, §8.1) — one press and the next operator is typing their own name
 * rather than inheriting the last one's attribution.
 *
 * This costs the shift change one extra click over the standing button that used to be here,
 * which was deliberate: §12.1 wanted it always visible. The menu replaces it on request, and
 * the trade is a rail that no longer spends two controls on something the operator does at
 * the start of a shift — the language picker moved to the system page for the same reason.
 * Attribution is unaffected either way: what protects it is that switching still forces the
 * chooser.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet } from "react-router-dom";
import { api } from "@/api/endpoints";
import { BrandMark } from "@/components/BrandMark";
import { type Permissions, usePermissions } from "@/hooks/usePermissions";
import {
  CubeIcon,
  GearIcon,
  LogIcon,
  ScanIcon,
  SearchIcon,
  SwitchUserIcon,
  TreeIcon,
  UploadIcon,
  UsersIcon,
} from "./icons";

const anyone = () => true;

/** Each entry shows for the roles that can use its page (§12.4); a visitor's rail has no
 * filing, an engineer's no audit log or users. */
const SECTIONS = [
  {
    key: "work",
    items: [
      {
        to: "/workbench/archive-create",
        label: "nav.workbench",
        Icon: ScanIcon,
        shown: (p: Permissions) => p.canRecord,
      },
      {
        to: "/workbench/archive-import",
        label: "nav.archiveImport",
        Icon: UploadIcon,
        shown: (p: Permissions) => p.canRecord,
      },
      { to: "/archives", label: "nav.archives", Icon: SearchIcon, shown: anyone },
    ],
  },
  {
    key: "masterData",
    items: [
      { to: "/product-types", label: "nav.productTypes", Icon: CubeIcon, shown: anyone },
      { to: "/boms", label: "nav.boms", Icon: TreeIcon, shown: anyone },
    ],
  },
  {
    key: "system",
    items: [
      { to: "/audit", label: "nav.audit", Icon: LogIcon, shown: (p: Permissions) => p.isAdmin },
      { to: "/users", label: "nav.users", Icon: UsersIcon, shown: (p: Permissions) => p.isAdmin },
      { to: "/system", label: "nav.system", Icon: GearIcon, shown: anyone },
    ],
  },
] as const;

export function Layout() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const permissions = usePermissions();
  const [menuOpen, setMenuOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  const logout = useMutation({
    mutationFn: api.logout,
    // `onSettled`, not `onSuccess`: a failed revoke still means the operator intended to
    // leave, and stranding them signed in at a station they are walking away from is the
    // worse failure. Where they land is the whole difference between the two menu items,
    // so it is the mutation's argument.
    onSettled: (_data, _error, destination: string) => window.location.assign(destination),
  });

  const email = me.data?.email ?? "";

  // A menu that only closes by choosing something is a trap — the operator who opened it by
  // accident has nothing to press. Escape and a click anywhere else both close it.
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [menuOpen]);

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <BrandMark />
          <span className="brand-text">
            <strong>Cybertron</strong>
            <small>{t("app.subtitle")}</small>
          </span>
        </div>

        <nav className="sidebar-nav">
          {SECTIONS.map((section) => (
            <div className="nav-group" key={section.key}>
              <p className="nav-group-label">{t(`nav.group.${section.key}`)}</p>
              {section.items
                .filter((item) => item.shown(permissions))
                .map(({ to, label, Icon }) => (
                  <NavLink key={to} to={to} className="nav-item">
                    <Icon />
                    <span>{t(label)}</span>
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer" ref={accountRef}>
          {menuOpen && (
            // Above the button rather than below it: the button sits at the bottom edge of
            // the viewport, and a menu opening downwards would be off-screen.
            <div className="user-menu" role="menu" aria-label={t("nav.accountMenu")}>
              <button
                type="button"
                role="menuitem"
                className="user-menu-item"
                onClick={() => logout.mutate("/login")}
              >
                {t("nav.signOut")}
              </button>
              <button
                type="button"
                role="menuitem"
                className="user-menu-item"
                onClick={() => logout.mutate("/auth/login")}
              >
                <SwitchUserIcon size={15} />
                <span>{t("nav.switchUser")}</span>
              </button>
            </div>
          )}
          <button
            type="button"
            className="current-user"
            title={email}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="avatar" aria-hidden="true">
              {email.slice(0, 1).toUpperCase() || "?"}
            </span>
            <span className="current-user-text">
              <span className="current-user-email">{email}</span>
              {me.data && <span className="current-user-role">{t(`roles.${me.data.role}`)}</span>}
            </span>
          </button>
        </div>
      </aside>

      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}
